// utils/audioUploadQueue.js

const PENDING_UPLOADS_KEY = 'pending_audio_uploads';
const MAX_RETRIES = 3;
const RETRY_DELAYS = [2000, 5000, 10000];

// IndexedDB setup for Gatsby (browser-only)
const openAudioDB = () => {
    if (typeof window === 'undefined') return null;
    
    return new Promise((resolve, reject) => {
        const request = indexedDB.open('AudioRecordings', 1);
        
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
        
        request.onupgradeneeded = (event) => {
            const db = event.target.result;
            if (!db.objectStoreNames.contains('recordings')) {
                db.createObjectStore('recordings', { keyPath: 'id' });
            }
        };
    });
};

const saveAudioToIndexedDB = async (id, blob, metadata) => {
    if (typeof window === 'undefined') return;
    
    const db = await openAudioDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction(['recordings'], 'readwrite');
        const store = transaction.objectStore('recordings');
        const request = store.put({ 
            id, 
            blob, 
            metadata,
            timestamp: Date.now() 
        });
        
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
    });
};

const getAudioFromIndexedDB = async (id) => {
    if (typeof window === 'undefined') return null;
    
    const db = await openAudioDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction(['recordings'], 'readonly');
        const store = transaction.objectStore('recordings');
        const request = store.get(id);
        
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
};

const deleteAudioFromIndexedDB = async (id) => {
    if (typeof window === 'undefined') return;
    
    const db = await openAudioDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction(['recordings'], 'readwrite');
        const store = transaction.objectStore('recordings');
        const request = store.delete(id);
        
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
    });
};

// LocalStorage queue management
const getPendingUploads = () => {
    if (typeof window === 'undefined') return [];
    try {
        const pending = localStorage.getItem(PENDING_UPLOADS_KEY);
        return pending ? JSON.parse(pending) : [];
    } catch (error) {
        console.error('Failed to get pending uploads:', error);
        return [];
    }
};

const addPendingUpload = (uploadData) => {
    if (typeof window === 'undefined') return;
    try {
        const pending = getPendingUploads();
        pending.push({
            ...uploadData,
            timestamp: Date.now(),
            retryCount: 0,
        });
        localStorage.setItem(PENDING_UPLOADS_KEY, JSON.stringify(pending));
    } catch (error) {
        console.error('Failed to add pending upload:', error);
    }
};

const removePendingUpload = (id) => {
    if (typeof window === 'undefined') return;
    try {
        const pending = getPendingUploads();
        const filtered = pending.filter(item => item.id !== id);
        localStorage.setItem(PENDING_UPLOADS_KEY, JSON.stringify(filtered));
    } catch (error) {
        console.error('Failed to remove pending upload:', error);
    }
};

const updateRetryCount = (id, count) => {
    if (typeof window === 'undefined') return;
    try {
        const pending = getPendingUploads();
        const updated = pending.map(item => 
            item.id === id ? { ...item, retryCount: count } : item
        );
        localStorage.setItem(PENDING_UPLOADS_KEY, JSON.stringify(updated));
    } catch (error) {
        console.error('Failed to update retry count:', error);
    }
};

// Retry logic with exponential backoff
const retryUpload = async (uploadFn, retryCount = 0) => {
    try {
        await uploadFn();
        return { success: true };
    } catch (error) {
        if (retryCount < MAX_RETRIES) {
            const delay = RETRY_DELAYS[retryCount];
            console.log(`Retry attempt ${retryCount + 1} after ${delay}ms lublubdubdub`);
            await new Promise(resolve => setTimeout(resolve, delay));
            return retryUpload(uploadFn, retryCount + 1);
        }
        return { success: false, error };
    }
};

export {
    openAudioDB,
    saveAudioToIndexedDB,
    getAudioFromIndexedDB,
    deleteAudioFromIndexedDB,
    getPendingUploads,
    addPendingUpload,
    removePendingUpload,
    updateRetryCount,
    retryUpload,
};
